import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle, XCircle, X, Info } from 'lucide-react';

const ToastContext = createContext(null);

let idCounter = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const remove = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const show = useCallback((type, message, duration = 3500) => {
    const id = ++idCounter;
    setToasts(prev => [...prev, { id, type, message }]);
    if (duration > 0) setTimeout(() => remove(id), duration);
  }, [remove]);

  const api = {
    success: (msg, d) => show('success', msg, d),
    error:   (msg, d) => show('error', msg, d),
    info:    (msg, d) => show('info', msg, d),
  };

  const iconFor = (type) => {
    if (type === 'success') return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (type === 'error')   return <XCircle className="w-5 h-5 text-red-500" />;
    return <Info className="w-5 h-5 text-blue-500" />;
  };

  const borderFor = (type) => {
    if (type === 'success') return 'border-green-200';
    if (type === 'error')   return 'border-red-200';
    return 'border-blue-200';
  };

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-sm">
        {toasts.map(t => (
          <div
            key={t.id}
            className={`bg-white border ${borderFor(t.type)} shadow-lg px-4 py-3 flex items-start gap-3 min-w-[280px] animate-in slide-in-from-right`}
          >
            {iconFor(t.type)}
            <p className="flex-1 text-sm text-gray-800">{t.message}</p>
            <button
              onClick={() => remove(t.id)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
};
